import { React, useState } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import PriceCheckRoundedIcon from '@mui/icons-material/PriceCheckRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatCurrency } from '../../utils/stringFormat';
import '../css/fineCharge.css';

const SELECT_ALL_OPTION = 'Selecionar Todas';

const CobrarMulta = () => {
    const [dados, setDados] = useState([]);
    const [clienteId, setClienteId] = useState('');
    const [cliente, setCliente] = useState('');
    const [unidades, setUnidades] = useState([]);
    const [valorMulta, setValorMulta] = useState(0);
    const [distrato, setDistrato] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    async function getClientInfo () {
        if (!clienteId) {
            return;
        }

        const body = JSON.stringify({
            cliente_id: clienteId,
            tipo: 'contato'
        });

        const response = await fetchFlask('getClientData', body);

        if (response.status !== 'error') {
            setDados(response || []);
            setUnidades([]);
            setValorMulta(0);
            setCliente(response[0].cliente);
        } else {
            handleAlerta(response);
        }
    };

    const unidadesOptions = [...new Set(dados.map((option) => option.unidade + ' | ' + option.doc))];
    const autocompleteOptions = unidadesOptions.length > 0 ? [SELECT_ALL_OPTION, ...unidadesOptions] : [];
    const selectedUnits = unidades.filter((unidade) => unidade !== SELECT_ALL_OPTION);

    const selectUnidades = (e, values) => {
        if (values.includes(SELECT_ALL_OPTION)) {
            setUnidades(unidadesOptions);
        } else {
            setUnidades(values);
        }
    };

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    async function submitCalcularMulta (e) {
        e.preventDefault();
        setIsSubmit(true);

        const unidadesFetch = [];
        for (const unidade of selectedUnits) {
            unidadesFetch.push(unidade.split(' | ')[0]);
        }

        const body = JSON.stringify({
            unidades: unidadesFetch,
            cliente_id: clienteId,
            distrato
        });

        try {
            const response = await fetchFlask('calcularMulta', body);
            if (response.status === 'success') {
                setValorMulta(parseFloat(response.message));
                setOpenDialog(true);
                return;
            }
            handleAlerta(response);
        } finally {
            setIsSubmit(false);
        }
    };

    async function submitCobrarMulta (e) {
        e.preventDefault();
        setIsSubmit(true);

        const unidadesFetch = [];
        for (const unidade of selectedUnits) {
            unidadesFetch.push(unidade.split(' | ')[0]);
        }

        const body = JSON.stringify({
            valor: valorMulta,
            unidades: unidadesFetch,
            cliente_id: clienteId,
            distrato
        });

        try {
            const response = await fetchFlask('cobrarMulta', body);
            if (response.status === 'success') {
                setOpenDialog(false);
                setUnidades([]);
                setValorMulta(0);
            }
            handleAlerta(response);
        } finally {
            setIsSubmit(false);
        }
    };

    const valorMultaLabel = valorMulta ? formatCurrency(valorMulta) : 'A calcular';
    const completedFields = [
        clienteId,
        cliente,
        selectedUnits.length > 0
    ].filter(Boolean).length;

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />

            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                PaperProps={{ className: 'fine-charge-dialog' }}
            >
                <DialogTitle>
                    Confirmar cobrança de multa
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`Valor calculado: ${formatCurrency(valorMulta)}`}
                    </DialogContentText>
                    <DialogContentText>
                        Revise cliente, unidades e contexto antes de confirmar a cobrança.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='contained'
                        disabled={isSubmit}
                        onClick={(e) => submitCobrarMulta(e)}
                        startIcon={<PaidRoundedIcon />}
                        endIcon={isSubmit && <CircularProgress size={20} />}
                    >
                        Confirmar
                    </Button>
                    <Button variant='outlined' onClick={() => setOpenDialog(false)}>Cancelar</Button>
                </DialogActions>
            </Dialog>

            <PageHeader>Cobrar Multa</PageHeader>

            <form action='' method='POST' id='formCobrarMulta' onSubmit={submitCalcularMulta} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Cálculo e cobrança de multa contratual</h2>
                        </Box>
                        <p>
                            Busque o cliente, selecione as unidades impactadas e calcule a multa antes de confirmar a cobrança.
                        </p>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <ManageAccountsRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Cliente e unidades</strong>
                            </Box>
                        </Box>

                        <Box className='ui-lookup-grid'>
                            <TextField
                                required
                                label='Matrícula / Cliente ID'
                                value={clienteId}
                                onChange={(e) => setClienteId(e.target.value)}
                            />
                            <Button
                                type='button'
                                variant='contained'
                                onClick={getClientInfo}
                                startIcon={<PersonSearchRoundedIcon />}
                            >
                                Buscar Cliente
                            </Button>
                        </Box>

                        <Box className='fine-charge-client-strip'>
                            <ReceiptLongRoundedIcon />
                            <Box>
                                <span>Cliente localizado</span>
                                <strong>{cliente || 'Aguardando busca'}</strong>
                            </Box>
                        </Box>

                        <Autocomplete
                            className='fine-charge-units-field'
                            multiple
                            disableCloseOnSelect
                            id='unidades'
                            limitTags={8}
                            value={unidades}
                            options={autocompleteOptions}
                            onChange={selectUnidades}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Unidades'
                                    required={selectedUnits.length === 0}
                                />
                            )}
                        />
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <GavelRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Contexto da cobrança</strong>
                            </Box>
                        </Box>

                        <Box className='fine-charge-checkbox-card'>
                            <FormControlLabel
                                className='fine-charge-checkbox-control'
                                control={
                                    <Checkbox
                                        checked={distrato}
                                        onChange={(e) => setDistrato(e.target.checked)}
                                    />
                                }
                                label={
                                    <Box className='fine-charge-checkbox-copy'>
                                        <strong>Troca de titularidade</strong>
                                        <span>Marque quando a multa estiver vinculada ao processo de troca de titularidade.</span>
                                    </Box>
                                }
                            />
                        </Box>
                    </Box>

                    <Box className='ui-rail'>
                        <Box>
                            <ReportProblemRoundedIcon />
                            <span>Unidades selecionadas</span>
                            <strong>{selectedUnits.length}</strong>
                        </Box>
                        <Box>
                            <PriceCheckRoundedIcon />
                            <span>Valor da multa</span>
                            <strong>{valorMultaLabel}</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do cálculo</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Matrícula / Cliente ID</span>
                            <strong className={!clienteId ? 'is-empty' : undefined}>{clienteId || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Cliente</span>
                            <strong className={!cliente ? 'is-empty' : undefined}>{cliente || 'Não localizado'}</strong>
                        </Box>
                        <Box>
                            <span>Unidades</span>
                            <strong>{selectedUnits.length} selecionada{selectedUnits.length === 1 ? '' : 's'}</strong>
                        </Box>
                        <Box>
                            <span>Contexto</span>
                            <strong>{distrato ? 'Troca de titularidade' : 'Cobrança padrão'}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-attention'>
                        <ReportProblemRoundedIcon />
                        <p>
                            O primeiro envio apenas calcula a multa. A cobrança definitiva acontece somente após a confirmação do valor calculado.
                        </p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/3 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<CalculateRoundedIcon />}>
                        Calcular Multa
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default CobrarMulta;
